import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiErrorMessage, API } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck,
  IdentificationBadge,
  Phone,
  Envelope,
  MapPin,
  CalendarBlank,
  UserCircle,
  Copy,
  ArrowLeft,
  Fingerprint,
} from "@phosphor-icons/react";

const ID_LABEL = {
  aadhaar: "Aadhaar",
  pan: "PAN Card",
  dl: "Driving Licence",
  passport: "Passport",
};

function Field({ label, value, mono, testId }) {
  return (
    <div className="flex flex-col gap-1 py-3 border-b border-white/[0.04] last:border-b-0">
      <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        {label}
      </span>
      <span
        data-testid={testId}
        className={`text-neutral-100 text-sm ${mono ? "font-mono tracking-wider" : ""}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function CopyBadge({ label, value, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value || "");
          toast.success(`${label} copied`);
        } catch {
          toast.error("Copy failed");
        }
      }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-indigo-400/30 bg-indigo-500/[0.08] text-[11px] text-indigo-200 hover:bg-indigo-500/[0.14] transition-colors"
      title={`Copy ${label}`}
    >
      <span className="font-mono">{value}</span>
      <Copy size={11} weight="bold" />
    </button>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [selfieUrl, setSelfieUrl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/identity/status");
        setStatus(data);
        const path = data?.identity?.selfie_storage_path;
        if (path) {
          const url = `${API}/identity/file?path=${encodeURIComponent(path)}`;
          const res = await fetch(url, { credentials: "include" });
          if (res.ok) {
            const blob = await res.blob();
            setSelfieUrl(URL.createObjectURL(blob));
          }
        }
      } catch (err) {
        toast.error(apiErrorMessage(err));
      }
    })();
    return () => {
      if (selfieUrl) URL.revokeObjectURL(selfieUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identity = status?.identity || {};
  const verified = !!identity.completed_at;

  return (
    <div data-testid="profile-root" className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1 mx-auto max-w-4xl px-5 md:px-8 py-8 md:py-12 w-full">
        <button
          className="text-sm text-neutral-500 hover:text-neutral-200 flex items-center gap-1.5 mb-6"
          onClick={() => nav("/")}
        >
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </button>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-2">
            My profile
          </div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
            Your verified LeLe identity
          </h1>
          <p className="text-neutral-400 mt-2 text-sm">
            This is the master profile attached to every loan you create or sign.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 lele-card p-6 md:p-8 mb-6"
        >
          {/* Selfie / avatar */}
          <div className="flex md:flex-col items-center gap-4">
            <div className="h-32 w-32 md:h-40 md:w-40 rounded-2xl border border-indigo-400/30 bg-indigo-500/[0.06] overflow-hidden grid place-items-center relative">
              {selfieUrl ? (
                <img
                  src={selfieUrl}
                  alt="Verified selfie"
                  className="h-full w-full object-cover"
                  data-testid="profile-selfie"
                />
              ) : (
                <UserCircle size={64} weight="duotone" className="text-indigo-300" />
              )}
              {verified && (
                <div className="absolute bottom-2 right-2 h-7 w-7 rounded-full bg-emerald-500/90 grid place-items-center border-2 border-neutral-950">
                  <ShieldCheck size={13} weight="fill" className="text-white" />
                </div>
              )}
            </div>
            <div className="md:text-center">
              <div
                data-testid="profile-verified-name"
                className="font-heading text-xl text-neutral-100"
              >
                {identity.full_name || status?.name || user?.name || "—"}
              </div>
              {status?.lele_id && (
                <div className="mt-1.5 flex md:justify-center">
                  <CopyBadge
                    label="LeLe ID"
                    value={status.lele_id}
                    testId="profile-lele-id-copy"
                  />
                </div>
              )}
              <div className="mt-2">
                <span
                  data-testid="profile-verified-chip"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] ${
                    verified
                      ? "border-emerald-400/30 bg-emerald-500/[0.08] text-emerald-200"
                      : "border-amber-400/30 bg-amber-500/[0.08] text-amber-200"
                  }`}
                >
                  <ShieldCheck size={11} weight="fill" />
                  {verified ? "LeLe Identity verified" : "Identity pending"}
                </span>
              </div>
            </div>
          </div>

          {/* Fields */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3 flex items-center gap-2">
              <IdentificationBadge size={13} weight="fill" className="text-indigo-400" />
              Government ID
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <Field
                label="ID type"
                value={ID_LABEL[identity.id_type] || (identity.id_type || "").toUpperCase()}
                testId="profile-id-type"
              />
              <Field
                label="ID reference (masked)"
                value={identity.id_number_masked}
                mono
                testId="profile-id-masked"
              />
              <Field label="Date of birth" value={identity.dob} testId="profile-dob" />
              <Field
                label="Gender"
                value={(identity.gender || "").replace(/^./, (c) => c.toUpperCase())}
                testId="profile-gender"
              />
              <div className="md:col-span-2">
                <Field label="Address on ID" value={identity.address} testId="profile-address" />
              </div>
            </div>

            <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mt-6 mb-3 flex items-center gap-2">
              <Fingerprint size={13} weight="fill" className="text-fuchsia-400" />
              Verification metadata
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <Field
                label="Verified on"
                value={
                  identity.verified_at
                    ? new Date(identity.verified_at).toLocaleString("en-IN")
                    : null
                }
              />
              <Field
                label="Face match with ID"
                value={
                  typeof identity.face_match_score === "number"
                    ? `${Math.round((identity.face_match_score || 0) * 100)}%${
                        identity.face_matched ? " · matched" : ""
                      }`
                    : null
                }
              />
              <Field
                label="Captured location"
                value={
                  identity.gps
                    ? `${Number(identity.gps.lat).toFixed(4)}, ${Number(identity.gps.lng).toFixed(4)}`
                    : null
                }
              />
              <Field
                label="Timezone at signup"
                value={identity.device?.timezone}
              />
            </div>
          </div>
        </motion.div>

        {/* Account */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lele-card p-6 md:p-8"
        >
          <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3 flex items-center gap-2">
            <UserCircle size={13} weight="fill" className="text-indigo-400" />
            Account
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Field
              label={
                <span className="inline-flex items-center gap-1.5">
                  <Envelope size={11} weight="fill" /> Email
                </span>
              }
              value={status?.email}
              testId="profile-email"
            />
            <Field
              label={
                <span className="inline-flex items-center gap-1.5">
                  <Phone size={11} weight="fill" /> Mobile
                </span>
              }
              value={status?.phone}
              testId="profile-phone"
            />
            <Field
              label={
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={11} weight="fill" /> LeLe ID
                </span>
              }
              value={status?.lele_id}
              mono
            />
            <Field
              label={
                <span className="inline-flex items-center gap-1.5">
                  <CalendarBlank size={11} weight="fill" /> Role
                </span>
              }
              value={user?.role === "admin" ? "Admin" : "Member"}
            />
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
